const makeString = (length) => {
  let result = ''
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  const charactersLength = characters.length
  let counter = 0
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
    counter += 1
  }
  return result
}

/**
 * Formats a numeric value as currency
 * @param {number|string} value - The value to format
 * @param {Object} options - Formatting options
 * @param {string} options.symbol - Currency symbol (default: '$')
 * @param {boolean} options.useLocale - Use locale formatting with thousands separators (default: false)
 * @param {string} options.nullValue - Value to return for null/undefined (default: '$0.00')
 * @returns {string} The formatted currency string
 */
const formatCurrency = (value, options = {}) => {
  const {
    symbol = '$',
    useLocale = false,
    nullValue = '$0.00'
  } = options

  if (value === undefined || value === null) {
    return nullValue
  }

  const numericValue = parseFloat(value)
  
  if (useLocale) {
    const formatted = numericValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
    return `${symbol} ${formatted}`
  }

  return symbol + numericValue.toFixed(2)
}

/**
 * Generates the appropriate legal disclaimer based on what input fields are shown
 * @param {Object} inputs - The inputs configuration object
 * @param {boolean} inputs.ShowEmail - Whether email input is shown
 * @param {boolean} inputs.ShowPhone - Whether phone input is shown
 * @param {string} companyName - The company name to use in the disclaimer (defaults to [company name])
 * @returns {string} The appropriate disclaimer text
 */
function getDisclaimerText(inputs = {}, companyName = '[company name]') {
  // Handle null or undefined inputs
  if (!inputs || typeof inputs !== 'object') {
    inputs = {}
  }
  
  const showEmail = inputs.ShowEmail === true || inputs.hasOwnProperty('ShowEmail') && inputs.ShowEmail !== false
  const showPhone = inputs.ShowPhone === true || inputs.hasOwnProperty('ShowPhone') && inputs.ShowPhone !== false

  // Both email and phone
  if (showEmail && showPhone) {
    return `By submitting this form and signing up for texts and/or emails, you consent to receive marketing messages (e.g. promos, cart reminders) from ${companyName} at the number and/or email provided, including messages sent by autodialer. Consent is not a condition of purchase. Msg & data rates may apply. Msg frequency varies. Unsubscribe at any time by replying STOP or clicking the unsubscribe link (where available).`
  }
  
  // Phone only
  if (showPhone && !showEmail) {
    return `By submitting this form and signing up for texts, you consent to receive marketing text messages (e.g. promos, cart reminders) from ${companyName} at the number provided, including messages sent by autodialer. Consent is not a condition of purchase. Msg & data rates may apply. Msg frequency varies. Unsubscribe at any time by replying STOP or clicking the unsubscribe link (where available).`
  }
  
  // Email only
  if (showEmail && !showPhone) {
    return `By submitting this form, you consent to receive marketing email messages (e.g. promos, cart reminders) from ${companyName} at the email provided. Consent is not a condition of purchase. Unsubscribe at any time by clicking the unsubscribe link (where available).`
  }

  // Default to email only if neither is explicitly shown
  return `By submitting this form, you consent to receive marketing email messages (e.g. promos, cart reminders) from ${companyName} at the email provided. Consent is not a condition of purchase. Unsubscribe at any time by clicking the unsubscribe link (where available).`
}

/**
 * Updates an existing disclaimer text to match current input configuration
 * This is useful for migrating old configurations
 * @param {Object} inputs - The inputs configuration object
 * @param {string} currentDisclaimer - The current disclaimer text (optional, for backward compatibility)
 * @returns {string} The appropriate disclaimer text
 */
function updateDisclaimerForInputs(inputs = {}, currentDisclaimer = '') {
  // Check if disclaimer is already correctly formatted or should be updated
  return getDisclaimerText(inputs, '[company name]')
}

export {
  makeString,
  formatCurrency,
  getDisclaimerText,
  updateDisclaimerForInputs,
}
